import { FC, useState } from 'react';
import { CircularProgress, DialogTitle, Divider, Stack } from '@mui/material';
import { z } from 'zod';
import { FormattedMessage, useIntl } from 'react-intl';
import { useModalContext } from '../../../modal/ModalContext';
import { graphql } from '../../../gql';
import { useApiMutation, useApiQuery } from '../../../query/hooks';
import { CreateMetricStepBasics, SubmitValuesCreateMetricStepBasics } from './CreateMetricStepBasics';
import { CreateMetricActions } from './CreateMetricActions';
import { CreateMetricStepper } from './CreateMetricStepper';
import { CreateMetricStepSetup, SubmitValuesCreateMetricStepSetup } from './CreateMetricStepSetup';
import { CreateMetricStepLocation, SubmitValuesCreateMetricStepLocation } from './CreateMetricStepLocation';
import { mutationResponseHandler } from '../../../utils/mutationResponseHandler';
import { useSnackbar } from '../../../store/snackbar';

const getManualMetricSourceQuery = graphql(`
	query connections_getManualMetricSource {
		listMetricSource(filter: { type: { eq: manual } }, limit: 1) {
			id
		}
	}
`);

const getMetricTemplateQuery = graphql(`
	query connections_getMetricTemplate($templateId: UUID!) {
		getMetricTemplate(by: { id: $templateId }) {
			behavior
		}
	}
`);

const createContentMetricMutation = graphql(`
	mutation connections_createMetric($data: ContentMetricCreateInput!) {
		createContentMetric(data: $data) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

const updateContentMetricMutation = graphql(`
	mutation createMetricModal_updateMetric($id: UUID!, $data: ContentMetricUpdateInput!) {
		updateContentMetric(by: { id: $id }, data: $data) {
			ok
			errorMessage
		}
	}
`);

const createMetricModalSchema = z.object({
	activityId: z.string().optional(),
});

type Props = z.infer<typeof createMetricModalSchema>;

type Step = 'basics' | 'setup' | 'location';

const CreateMetricModal: FC<Props> = ({ activityId }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();

	const [step, setStep] = useState<Step>('basics');
	const [submitValuesStepBasics, setSubmitValuesStepBasics] = useState<SubmitValuesCreateMetricStepBasics>();
	const [submitValuesStepSetup, setSubmitValuesStepSetup] = useState<SubmitValuesCreateMetricStepSetup>();

	const [isCompleteStepLocation, setIsCompleteStepLocation] = useState(false);

	const isSelectedExistingTemplate = submitValuesStepBasics?.template.id != null;

	const { data: getManualMetricSourceResult } = useApiQuery(getManualMetricSourceQuery);

	const manualSourceId = getManualMetricSourceResult?.listMetricSource[0]?.id;
	const { openSnackbar } = useSnackbar();

	const { data: getMetricTemplateResult } = useApiQuery(getMetricTemplateQuery, {
		enabled: isSelectedExistingTemplate,
		variables: { templateId: submitValuesStepBasics?.template.id ?? '' },
	});

	const behavior = isSelectedExistingTemplate ? getMetricTemplateResult?.getMetricTemplate?.behavior : submitValuesStepSetup?.behavior;

	const { mutateAsync: createContentMetricAsync, isPending: isCreateContentMetricPending } = useApiMutation(createContentMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.createContentMetric.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.createContentMetric.errorMessage,
				openSnackbar,
			});
		},
	});

	const { mutateAsync: updateContentMetricAsync, isPending: isUpdateContentMetricPending } = useApiMutation(updateContentMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updateContentMetric.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				callback: closeModal,
				openSnackbar,
			});
		},
	});

	const handleSubmitStepBasics = (values: SubmitValuesCreateMetricStepBasics) => {
		setSubmitValuesStepBasics(values);
		// skip setup if existing template has been selected
		setStep(values.template.id == null ? 'setup' : 'location');
	};

	const handleSubmitStepSetup = (values: SubmitValuesCreateMetricStepSetup) => {
		setSubmitValuesStepSetup(values);
		setStep('location');
	};

	const handleSubmitStepLocation = async (values: SubmitValuesCreateMetricStepLocation) => {
		const createContentMetricResult = await createContentMetricAsync({
			data: {
				metric: {
					create: {
						name: submitValuesStepBasics?.metricName,
						behavior,
						dataSet: {
							create: {
								name: submitValuesStepBasics?.metricName, // intentionally same name as metric
								source: {
									connect: {
										id: manualSourceId,
									},
								},
								metricTemplate: isSelectedExistingTemplate
									? {
											connect: {
												id: submitValuesStepBasics.template.id,
											},
										}
									: {
											create: {
												name: submitValuesStepBasics?.template.name,
												behavior: submitValuesStepSetup?.behavior,
												precision: submitValuesStepSetup?.precision,
												expectations: submitValuesStepSetup?.expectations,
												defaultTrendComparison: 'relative', // not configurable for now
												unit: submitValuesStepSetup?.unit,
												unitSign: submitValuesStepSetup?.unitSign,
											},
										},
							},
						},
					},
				},
			},
		});

		if (createContentMetricResult.createContentMetric.node?.id != null && values.activityId != null) {
			await updateContentMetricAsync({
				id: createContentMetricResult.createContentMetric.node.id,
				data: {
					activities: [
						{
							connect: {
								id: values.activityId,
							},
						},
						{
							update: {
								by: {
									id: values.activityId,
								},
								data: {
									touchpoint: {
										update: {
											phase: {
												update: {
													contentMetrics: [
														{
															connect: {
																id: createContentMetricResult.createContentMetric.node.id,
															},
														},
													],
												},
											},
										},
									},
								},
							},
						},
					],
				},
			});
		}

		closeModal();
	};

	const handleBack = () => {
		setStep((currentStep) => {
			if (currentStep === 'location' && submitValuesStepBasics?.template.id == null) {
				setSubmitValuesStepSetup(undefined);
				return 'setup';
			}

			setSubmitValuesStepBasics(undefined);
			return 'basics';
		});
	};

	const stepper = <CreateMetricStepper step={step} skipSetup={isSelectedExistingTemplate} />;

	const actions = (
		<CreateMetricActions
			isLoading={isCreateContentMetricPending || isUpdateContentMetricPending || (step === 'location' && behavior == null)}
			isFirst={step === 'basics'}
			isLast={step === 'location'}
			showSkip={step === 'location' && !isCompleteStepLocation}
			onCancel={closeModal}
			onBack={handleBack}
		/>
	);

	return (
		<>
			<DialogTitle>
				<FormattedMessage id={'form.metric.create.title'} />
				{step !== 'basics' && <>&nbsp;{submitValuesStepBasics?.metricName}</>}
			</DialogTitle>
			<Divider />
			{manualSourceId == null ? (
				<Stack p={3} alignItems={'center'}>
					<CircularProgress />
				</Stack>
			) : (
				<>
					{step === 'basics' && <CreateMetricStepBasics stepper={stepper} actions={actions} onSubmit={handleSubmitStepBasics} />}
					{step === 'setup' && <CreateMetricStepSetup stepper={stepper} actions={actions} onSubmit={handleSubmitStepSetup} />}
					{step === 'location' && (
						<CreateMetricStepLocation
							stepper={stepper}
							actions={actions}
							onSubmit={handleSubmitStepLocation}
							activityId={activityId}
							onCompleteChange={setIsCompleteStepLocation}
						/>
					)}
				</>
			)}
		</>
	);
};

export { CreateMetricModal, createMetricModalSchema };
